import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, Renderer2, ViewChild } from '@angular/core';
import { MagnoliaService } from 'src/app/magnolia/magnolia.service';
import { DOCUMENT } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { AppConfig } from 'src/app/shared/app.config';

@Component({
    selector: 'bmt-help',
    templateUrl: './help.component.html',
    styleUrls: ['./help.component.scss'],
    standalone: false
})
export class HelpComponent implements AfterViewInit, OnDestroy {
  @ViewChild('helpContentContainer', { static: true }) private readonly helpContentContainer!: ElementRef<HTMLElement>;
  private removeHelpContentClickListener: (() => void) | null = null;

  helpContent = toSignal(
    this.magnoliaService.getMagnoliaContent('help').pipe(
      map(content => this.normalizeHelpMarkup(content))
    ),
    { initialValue: '' }
  );

  constructor(private readonly magnoliaService: MagnoliaService,
              private readonly config: AppConfig,
              private readonly renderer: Renderer2,
              @Inject(DOCUMENT) private readonly doc: Document) {
  }

  ngAfterViewInit(): void {
    this.removeHelpContentClickListener = this.renderer.listen(
      this.helpContentContainer.nativeElement,
      'click',
      (event: MouseEvent) => this.onHelpContentClick(event)
    );
  }

  ngOnDestroy(): void {
    this.removeHelpContentClickListener?.();
  }

  onHelpContentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    const link = target?.closest('a');
    if (!link || event.defaultPrevented) {
      return;
    }

    // Preserve browser-native open-in-new-tab/window interactions.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    const href = (link.getAttribute('href') || '').trim();
    if (!href) {
      return;
    }

    if (href.startsWith('#')) {
      event.preventDefault();
      this.scrollToHelpAnchor(href.substring(1));
      return;
    }

    const navigableUrl = this.extractNavigableUrl(href);
    if (!navigableUrl) {
      return;
    }

    const rewrittenUrl = this.rewriteCmsAssetUrl(navigableUrl);
    if (rewrittenUrl === navigableUrl) {
      return;
    }

    event.preventDefault();

    const resolvedHref = this.resolveHref(rewrittenUrl);
    const targetAttr = (link.getAttribute('target') || '').toLowerCase();
    if (targetAttr === '_blank') {
      this.doc.defaultView?.open(resolvedHref, '_blank', 'noopener,noreferrer');
      return;
    }

    this.doc.location.assign(resolvedHref);
  }

  private resolveHref(href: string): string {
    try {
      return new URL(href, this.doc.baseURI).toString();
    } catch {
      return href;
    }
  }

  private rewriteCmsAssetUrl(url: string): string {
    const trimmedUrl = (url || '').trim();
    if (!trimmedUrl) {
      return '';
    }

    // Only rewrite raw CMS paths; absolute http(s) URLs are preserved as-is.
    if (/^https?:\/\//i.test(trimmedUrl)) {
      return trimmedUrl;
    }

    const cmsPath = trimmedUrl.startsWith('/') ? trimmedUrl : `/${trimmedUrl}`;
    if (!cmsPath.toLowerCase().startsWith('/cms/')) {
      return trimmedUrl;
    }

    const magnoliaUrl = this.config?.magnoliaUrl || '';
    const cmsMarkerIndex = magnoliaUrl.toLowerCase().indexOf('/cms/');
    const proxyPrefix = cmsMarkerIndex > -1 ? magnoliaUrl.substring(0, cmsMarkerIndex) : '';
    if (!proxyPrefix) {
      return cmsPath;
    }

    return `${proxyPrefix}${cmsPath}`;
  }

  private extractNavigableUrl(href: string): string {
    if (!this.isSafeHref(href) || href.startsWith('#')) {
      return '';
    }

    return href;
  }

  private normalizeHelpMarkup(rawHtml: string): string {
    if (!rawHtml) {
      return '';
    }

    const parsedDocument = new DOMParser().parseFromString(rawHtml, 'text/html');
    const container = this.doc.createElement('div');

    Array.from(parsedDocument.body.childNodes).forEach(node => {
      const sanitizedNode = this.sanitizeHelpNode(node);
      if (sanitizedNode) {
        container.appendChild(sanitizedNode);
      }
    });

    return container.innerHTML;
  }

  private sanitizeHelpNode(node: Node): Node | null {
    if (node.nodeType === Node.TEXT_NODE) {
      return this.doc.createTextNode(node.textContent || '');
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }

    const sourceElement = node as Element;
    const tagName = sourceElement.tagName.toLowerCase();
    if (this.isBlockedTag(tagName)) {
      return null;
    }

    const sanitizedElement = this.doc.createElement(tagName);
    this.copySafeAttributes(sourceElement, sanitizedElement, tagName === 'a');

    Array.from(sourceElement.childNodes).forEach(childNode => {
      const sanitizedChildNode = this.sanitizeHelpNode(childNode);
      if (sanitizedChildNode) {
        sanitizedElement.appendChild(sanitizedChildNode);
      }
    });

    return sanitizedElement;
  }

  private copySafeAttributes(sourceElement: Element, targetElement: Element, isAnchor: boolean): void {
    Array.from(sourceElement.attributes).forEach(attribute => {
      const attributeName = attribute.name.toLowerCase();
      if (attributeName.startsWith('on')) {
        return;
      }

      if (attributeName === 'style' && this.containsUnsafeInlineStyle(attribute.value)) {
        return;
      }

      if (!isAnchor && this.isUrlAttribute(attributeName) && !this.isSafeHref(attribute.value)) {
        return;
      }

      targetElement.setAttribute(attribute.name, attribute.value);
    });

    if (isAnchor) {
      this.normalizeAnchorAttributes(sourceElement, targetElement);
    }
  }

  private normalizeAnchorAttributes(sourceAnchor: Element, targetAnchor: Element): void {
    const href = (sourceAnchor.getAttribute('href') || '').trim();
    const onclick = sourceAnchor.getAttribute('onclick') || '';
    const extractedAction = this.extractAnchorAction(onclick);
    const nextHref = this.isSafeHref(href) ? href : extractedAction?.href || '';

    if (nextHref) {
      targetAnchor.setAttribute('href', nextHref);
    } else {
      targetAnchor.removeAttribute('href');
    }

    const target = (sourceAnchor.getAttribute('target') || extractedAction?.target || '').trim();
    if (target) {
      targetAnchor.setAttribute('target', target);
      if (target.toLowerCase() !== '_self') {
        targetAnchor.setAttribute('rel', this.mergeRelValues(sourceAnchor.getAttribute('rel'), ['noopener', 'noreferrer']));
      }
    } else {
      targetAnchor.removeAttribute('target');
    }
  }

  private extractAnchorAction(onclick: string): { href: string; target?: string } | null {
    const windowOpenToken = 'window.open';
    const locationHrefToken = ['window', 'location', 'href'].join('.');
    const locationAssignToken = ['location', 'assign'].join('.');
    const locationReplaceToken = ['location', 'replace'].join('.');

    const windowOpenIndex = onclick.indexOf(windowOpenToken);
    if (windowOpenIndex > -1) {
      const openCallStart = onclick.indexOf('(', windowOpenIndex);
      const hrefArgument = this.readQuotedArgument(onclick, openCallStart + 1);
      if (hrefArgument && this.isSafeHref(hrefArgument.value)) {
        const targetArgument = this.readQuotedArgument(onclick, hrefArgument.nextIndex + 1);
        return {
          href: hrefArgument.value,
          target: targetArgument?.value || '_blank'
        };
      }
    }

    const locationToken = [locationHrefToken, locationAssignToken, locationReplaceToken]
      .find(token => onclick.indexOf(token) > -1);
    if (!locationToken) {
      return null;
    }

    const assignmentStart = onclick.indexOf(locationToken) + locationToken.length;
    const nextQuoteIndex = this.findNextQuoteIndex(onclick, assignmentStart);
    if (nextQuoteIndex < 0) {
      return null;
    }

    const hrefArgument = this.readQuotedArgument(onclick, nextQuoteIndex);
    return hrefArgument && this.isSafeHref(hrefArgument.value)
      ? { href: hrefArgument.value }
      : null;
  }

  private readQuotedArgument(value: string, startIndex: number): { value: string; nextIndex: number } | null {
    const quoteIndex = this.findNextQuoteIndex(value, startIndex);
    if (quoteIndex < 0) {
      return null;
    }

    const quote = value[quoteIndex];
    let cursor = quoteIndex + 1;
    let collectedValue = '';
    while (cursor < value.length) {
      const currentCharacter = value[cursor];
      if (currentCharacter === '\\') {
        cursor += 1;
        if (cursor < value.length) {
          collectedValue += value[cursor];
        }
      } else if (currentCharacter === quote) {
        return { value: collectedValue.trim(), nextIndex: cursor + 1 };
      } else {
        collectedValue += currentCharacter;
      }

      cursor += 1;
    }

    return null;
  }

  private findNextQuoteIndex(value: string, startIndex: number): number {
    for (let index = Math.max(0, startIndex); index < value.length; index += 1) {
      const currentCharacter = value[index];
      if (currentCharacter === '\'' || currentCharacter === '"') {
        return index;
      }
    }

    return -1;
  }

  private isBlockedTag(tagName: string): boolean {
    return tagName === 'script'
      || tagName === 'iframe'
      || tagName === 'object'
      || tagName === 'embed'
      || tagName === 'applet'
      || tagName === 'base'
      || tagName === 'meta';
  }

  private isUrlAttribute(attributeName: string): boolean {
    return attributeName === 'href'
      || attributeName === 'src'
      || attributeName === 'xlink:href'
      || attributeName === 'action'
      || attributeName === 'formaction'
      || attributeName === 'poster';
  }

  private containsUnsafeInlineStyle(styleValue: string): boolean {
    const normalizedStyle = styleValue.toLowerCase();
    return normalizedStyle.includes('expression(')
      || normalizedStyle.includes('behavior:')
      || normalizedStyle.includes('-moz-binding')
      || this.containsUnsafeUrlFunction(normalizedStyle);
  }

  private containsUnsafeUrlFunction(styleValue: string): boolean {
    let searchStart = 0;
    while (searchStart < styleValue.length) {
      const urlIndex = styleValue.indexOf('url(', searchStart);
      if (urlIndex < 0) {
        return false;
      }

      const closeIndex = styleValue.indexOf(')', urlIndex + 4);
      if (closeIndex < 0) {
        return true;
      }

      const rawUrl = this.stripWrappingQuotes(styleValue.slice(urlIndex + 4, closeIndex).trim());
      if (!this.isSafeHref(rawUrl)) {
        return true;
      }

      searchStart = closeIndex + 1;
    }

    return false;
  }

  private isSafeHref(url: string): boolean {
    const trimmedUrl = (url || '').trim();
    if (!trimmedUrl) {
      return false;
    }

    if (trimmedUrl.startsWith('#')) {
      return true;
    }

    if (trimmedUrl.startsWith('/') || trimmedUrl.startsWith('./') || trimmedUrl.startsWith('../') || trimmedUrl.startsWith('?')) {
      return true;
    }

    try {
      const resolvedUrl = new URL(trimmedUrl, this.doc.baseURI || 'https://bmt.local');
      return resolvedUrl.protocol === 'http:' || resolvedUrl.protocol === 'https:';
    } catch {
      return false;
    }
  }

  private mergeRelValues(existingRel: string | null, requiredValues: string[]): string {
    const relValues = new Set((existingRel || '').split(/\s+/).filter(Boolean).map(value => value.toLowerCase()));
    requiredValues.forEach(value => relValues.add(value));
    return Array.from(relValues).join(' ');
  }

  private stripWrappingQuotes(value: string): string {
    if (!value) {
      return '';
    }

    const hasMatchingSingleQuotes = value.startsWith('\'') && value.endsWith('\'');
    const hasMatchingDoubleQuotes = value.startsWith('"') && value.endsWith('"');
    return hasMatchingSingleQuotes || hasMatchingDoubleQuotes
      ? value.slice(1, -1)
      : value;
  }


  private scrollToHelpAnchor(rawAnchor: string): void {
    const anchor = decodeURIComponent(rawAnchor).replace(/^#/, '').trim();
    if (!anchor) {
      return;
    }

    const element = this.doc.getElementById(anchor)
      || this.doc.querySelector(`a[name="${anchor.replace(/"/g, '\\"')}"]`);
    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (this.doc.defaultView) {
      this.doc.defaultView.location.hash = anchor;
    }
  }


}
